import React, { useContext } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl, defineMessages } from 'react-intl-next';

import { QueryGroup } from '@confluence/query-group';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { PageTitle } from '@confluence/page-title';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';

import { I18N_KEYS } from './i18n.ts';
import { getSpaceNameFromResults, getSpaceNameQueryProps } from './utils.ts';

const i18n = defineMessages({
	companyHubDefaultTitleI18n: {
		id: 'document-title.document-edit-page-title.company-hub.title',
		defaultMessage: 'Company hub',
		description: 'The default title for Company Hub in the absence of a set company hub name',
	},
	editName: {
		id: 'document-title.edit.name',
		defaultMessage: 'Edit',
		description: 'Edit page title prefix',
	},
	editTemplatePage: {
		id: 'document-title.edit.page.template',
		defaultMessage: 'Edit template',
		description: 'A title appears in browser title when editing a template',
	},
});

const toTitleString = (texts) => {
	return texts.filter((i) => i).join(' - ');
};

type DocumentEditPageTitleProps = {
	content?: any;
	contentType: 'blogpost' | 'page';
	isCreatingNew?: boolean;
	isTemplateEditPage?: boolean;
	isLivePage?: boolean;
	spaceKey: string;
	title: string | null;
};

export const DocumentEditPageTitle = injectIntl(
	({
		content = {},
		contentType = 'page',
		intl,
		isCreatingNew,
		isTemplateEditPage,
		isLivePage,
		spaceKey,
		title,
	}: DocumentEditPageTitleProps & WrappedComponentProps) => {
		const { siteTitle, companyHubName } = useContext(SPAViewContext);

		return (
			<TransparentErrorBoundary attribution={Attribution.INHERIT}>
				<QueryGroup queryProps={getSpaceNameQueryProps(spaceKey)}>
					{({ results, loading }) => {
						if (loading) {
							return null;
						}
						const spaceName = getSpaceNameFromResults(results);

						let docTitle;
						if (isCreatingNew) {
							const titleI18nKey = {
								id: (isTemplateEditPage
									? I18N_KEYS.CREATE_TEMPLATE_PAGE
									: I18N_KEYS.CREATE_FULL_EDIT_PAGE)[contentType],
							};
							const instruction = intl.formatMessage(titleI18nKey);
							docTitle = isTemplateEditPage
								? toTitleString([instruction, title, spaceName, siteTitle])
								: // new draft: title will be whatever string user type into title text field
									// unpublished draft: title is undefined, have to get title from content.title
									toTitleString([instruction, title || content?.title, spaceName, siteTitle]);
						} else {
							const titleI18n = isTemplateEditPage ? i18n.editTemplatePage : i18n.editName;
							const prefix = isLivePage ? '' : intl.formatMessage(titleI18n);
							const companyHubSpaceName =
								companyHubName || intl.formatMessage(i18n.companyHubDefaultTitleI18n);
							docTitle = isCompanyHubSpaceKey(spaceKey)
								? toTitleString([prefix, companyHubSpaceName, siteTitle])
								: toTitleString([prefix, title ?? content?.title, spaceName, siteTitle]);
						}
						// set for SSR document title
						(window as any).__DOCUMENT_TITLE_TAG__ = docTitle;
						return <PageTitle title={docTitle} />;
					}}
				</QueryGroup>
			</TransparentErrorBoundary>
		);
	},
);
